import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, Menu, X } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { COMPANY, PUBLIC_EMAIL } from "@/lib/constants";
import { useDomainBranding } from "@/hooks/useDomainBranding";
import { getMicroSiteContent } from "@/lib/microSiteContent";

interface Crumb { name: string; url: string }

interface Props {
  children: ReactNode;
  title: string;
  description: string;
  canonicalPath?: string; // path only, e.g. "/services"
  breadcrumbs?: Crumb[];
  jsonLd?: Record<string, unknown>[];
  /** Force noindex,nofollow (e.g. for unreviewed / draft microsite routes). */
  noindex?: boolean;
}

const MicroSiteLayout = ({ children, title, description, canonicalPath, breadcrumbs, jsonLd, noindex }: Props) => {
  const brand = useDomainBranding();
  const content = getMicroSiteContent(brand.brand);
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const path = canonicalPath ?? (location.pathname === "/" ? "" : location.pathname);
  const canonical = `${brand.canonicalOrigin}${path}`;
  const fullTitle = title.includes(brand.siteName) ? title : `${title} | ${brand.siteName}`;

  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((c, i) => ({
      "@type": "ListItem", position: i + 1, name: c.name, item: c.url,
    })),
  } : null;

  const navLinks: Array<{ to: string; label: string }> = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/about", label: "About" },
    { to: "/faq", label: "FAQ" },
    { to: "/quick-estimate", label: "Free Estimate" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <Helmet>
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        {noindex && <meta name="robots" content="noindex,nofollow" />}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={brand.siteName} />
        <meta property="og:image" content={brand.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        {breadcrumbSchema && (
          <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        )}
        {jsonLd?.map((entry, i) => (
          <script key={i} type="application/ld+json">{JSON.stringify(entry)}</script>
        ))}
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background border-b border-border shadow-sm">
          <div className="container mx-auto px-4 h-16 flex items-center justify-between">
            <Link to="/" className="font-heading font-bold text-lg md:text-xl text-foreground">
              {brand.siteName}
            </Link>
            <nav className="hidden md:flex items-center gap-1">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-2 text-sm font-medium text-foreground/80 hover:text-accent transition-colors"
                >
                  {l.label}
                </Link>
              ))}
              <a href={COMPANY.phoneHref} className="ml-2">
                <Button variant="default" size="sm">
                  <Phone className="mr-2 h-4 w-4" /> {COMPANY.phoneFormatted}
                </Button>
              </a>
            </nav>
            <button
              className="md:hidden p-2 text-foreground"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          {menuOpen && (
            <div className="md:hidden border-t border-border bg-background">
              <div className="container mx-auto px-4 py-2 flex flex-col">
                {navLinks.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setMenuOpen(false)}
                    className="py-2 text-sm font-medium text-foreground/80"
                  >
                    {l.label}
                  </Link>
                ))}
                <a href={COMPANY.phoneHref} className="py-3">
                  <Button variant="default" size="sm" className="w-full">
                    <Phone className="mr-2 h-4 w-4" /> {COMPANY.phoneFormatted}
                  </Button>
                </a>
              </div>
            </div>
          )}
        </header>

        <main className="flex-1">{children}</main>

        {/* Footer */}
        <footer className="bg-primary text-primary-foreground py-10 mt-12">
          <div className="container mx-auto px-4 grid md:grid-cols-3 gap-8">
            <div>
              <div className="font-heading font-bold text-lg mb-2">{brand.siteName}</div>
              <p className="text-sm text-primary-foreground/80 mb-3">{brand.tagline}</p>
              <p className="text-xs text-primary-foreground/70">
                An <strong>{COMPANY.name}</strong> brand • FL License {COMPANY.license}
              </p>
            </div>
            <div>
              <div className="font-semibold mb-2">Quick Links</div>
              <ul className="space-y-1 text-sm">
                {navLinks.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-primary-foreground/85 hover:text-accent">
                      {l.label}
                    </Link>
                  </li>
                ))}
                {content?.extraTools.includes("roof-visualizer") && (
                  <li><Link to="/roof-visualizer" className="text-primary-foreground/85 hover:text-accent">Roof Visualizer</Link></li>
                )}
                {content?.extraTools.includes("leak-detection") && (
                  <li><Link to="/leak-detection" className="text-primary-foreground/85 hover:text-accent">Leak Detection</Link></li>
                )}
                {content?.extraTools.includes("storm-damage-analyzer") && (
                  <li><Link to="/storm-damage-analyzer" className="text-primary-foreground/85 hover:text-accent">Storm Damage Analyzer</Link></li>
                )}
              </ul>
            </div>
            <div>
              <div className="font-semibold mb-2">Contact</div>
              <p className="text-sm">
                <a href={COMPANY.phoneHref} className="hover:text-accent">{COMPANY.phoneFormatted}</a>
              </p>
              {PUBLIC_EMAIL && (
                <p className="text-sm">
                  <a href={`mailto:${PUBLIC_EMAIL}`} className="hover:text-accent">{PUBLIC_EMAIL}</a>
                </p>
              )}
              <p className="text-sm text-primary-foreground/80 mt-2">
                Your request is handled directly by O'Brien Contracting, a Florida-licensed roofing
                contractor (CCC1335947).
              </p>
              <p className="mt-3">
                <a href="https://obriencontractingusa.com" className="text-xs underline text-primary-foreground/70 hover:text-accent">
                  Visit O'Brien Contracting →
                </a>
              </p>
              {brand.obcPillarUrl && brand.obcPillarLabel && (
                <p className="mt-2">
                  <a href={brand.obcPillarUrl} className="text-xs underline text-primary-foreground/70 hover:text-accent">
                    {brand.obcPillarLabel} →
                  </a>
                </p>
              )}
            </div>
          </div>
          <div className="container mx-auto px-4 mt-8 pt-6 border-t border-primary-foreground/15 text-xs text-primary-foreground/65">
            © {new Date().getFullYear()} {COMPANY.legalName}. All rights reserved.
          </div>
        </footer>
      </div>
    </>
  );
};

export default MicroSiteLayout;
